import { compareEnumValues, ComponentType, DateTimeFormatType, FrequencyFormat, TimeInterval } from '@Common/Enums';
import { IntervalType } from '@Common/Enums/Enum';
import { DateTimeInterval, DateTimeNamedFormat, Format, InsightMetaModel } from '@InstanceFactory';
import constants from '../Core/Constant';
import Maybe, { IMaybe } from '../Core/Maybe';

import localizationHelper from './LocalizationHelper';
import timeHelper from './TimeHelper';
import FormatDotNetStyle from '@Libs/FormatDotNetStyle';

export type IFormat = Format | string;

namespace formatHelper {
    export function getIntervalName(intervalType: TimeInterval): string {
        return TimeInterval.getName(intervalType) || '';
    }

    export function dateTimeFromIOSString(stringDate: string, showTime = false, timeOnly = false): Date {
        const a = stringDate.split(/[^0-9]/).map(s => parseFloat(s));
        return timeOnly ? new Date(1970, 0, 1, a[0], a[1]) : showTime ? new Date(a[0], a[1] - 1, a[2], a[3], a[4]) : new Date(a[0], a[1] - 1, a[2]);
    }

    export function formatMinutes(value: any, format: string) {
        if (_.isDate(value)) {
            value = value.getTime();
        }
        return formatSeconds(value * 60, format);
    }

    export function formatSeconds(value: any, format: string) {
        if (_.isDate(value)) {
            value = value.getTime();
        }
        return formatMilliseconds(value * 1000, format);
    }

    export function formatMilliseconds(value: any, format: string) {
        if (_.isDate(value)) {
            value = value.getTime();
        }
        return formatTimeSpan(value * 10000, format);
    }

    export function parseMilliseconds(value: string) {
        const formatter = new FormatDotNetStyle(localizationHelper.getTwoLettersLanguageName());

        return formatter.parseTimeSpan(value) / 10000;
    }

    export function formatTimeSpan(value: any, format: string) {
        if (_.isDate(value)) {
            value = value.getTime();
        }

        const formatter = new FormatDotNetStyle(localizationHelper.getTwoLettersLanguageName());
        return formatter.formatTimeSpan(format, value);
    }

    export function parseTimeSpan(value: string) {
        const formatter = new FormatDotNetStyle(localizationHelper.getTwoLettersLanguageName());
        return formatter.parseTimeSpan(value);
    }

    export function dateTimeToString(dateTime: Date): string {
        return formatHelper.formatValueByInvariantCulture(dateTime);
    }

    export function getDateFormat(maxDate: Date, minDate: Date, formatType: DateTimeFormatType, dimensionId: string): string {
        let formatString = constants.defaultDateTimeFormat;
        const intervalString = timeHelper.getIntervalString(dimensionId, true);

        if (maxDate instanceof Date && minDate instanceof Date) {
            let differentInterval = strings.timeDrillDownType_plural.minutes;
            if (maxDate.getFullYear() > minDate.getFullYear()) {
                differentInterval = strings.timeDrillDownType_plural.years;
            } else if (maxDate.getMonth() > minDate.getMonth()) {
                differentInterval = strings.timeDrillDownType_plural.months;
            } else if (maxDate.getDate() - minDate.getDate() >= 7 || maxDate.getDay() < minDate.getDay()) {
                differentInterval = strings.timeDrillDownType_plural.weeks;
            } else if (maxDate.getDate() > minDate.getDate()) {
                differentInterval = strings.timeDrillDownType_plural.days;
            } else if (maxDate.getHours() > minDate.getHours()) {
                differentInterval = strings.timeDrillDownType_plural.hours;
            } else if (maxDate.getMinutes() > minDate.getMinutes()) {
                differentInterval = strings.timeDrillDownType_plural.minutes;
            }

            if (compareEnumValues(intervalString, differentInterval, strings.timeDrillDownType_plural) > 0) {
                differentInterval = intervalString;
            }

            const formats = altosoft.model.insightMeta.dateTimeFormat.formats;
            for (const format of formats) {
                if (format.frequency === intervalString && format.interval === differentInterval) {
                    formatString = formatType === DateTimeFormatType.short ? format.shortFormat : format.longFormat;
                    break;
                }
            }
        }

        return formatString;
    }

    export function getFormat(formatName: string, defaultFormat?: string): IFormat {
        const formats = getMeta().read(m => m.numberFormats).value;
        return _.find(formats, f => f.name === formatName) || defaultFormat || constants.defaultNumberFormat;
    }

    const formatFrequencyOrder = [
        FrequencyFormat.seconds,
        FrequencyFormat.minutes,
        FrequencyFormat.hours,
        FrequencyFormat.days,
        FrequencyFormat.weeks,
        FrequencyFormat.months,
        FrequencyFormat.quarters,
        FrequencyFormat.years
    ];

    const intervalTypeOrder = [
        IntervalType.years,
        IntervalType.months,
        IntervalType.days,
        IntervalType.weeks,
        IntervalType.hours,
        IntervalType.minutes
    ];

    export function getDateTimeFormatTemplate(interval: IntervalType, frequency: FrequencyFormat, isShortFormat = false): string {
        const frequencyIndex = formatFrequencyOrder.indexOf(frequency);
        const intervalIndex = intervalTypeOrder.indexOf(interval);

        const shortFormatTable = [
            // year
            ['dd MMM yy HH:mm:ss', 'dd MMM yy HH:mm', 'dd MMM yy HH:mm', 'dd MMM yy', 'dd MMM yy', 'MMM yy', 'QQ FYY', 'FYY'],
            // month
            ['dd MMM HH:mm:ss', 'dd MMM HH:mm', 'dd MMM HH:mm', 'dd MMM', 'dd MMM', 'MMM', 'QQ FYY', 'FYY'],
            // week
            ['dd HH:mm:ss', 'dd HH:mm', 'dd HH:mm', 'dd', 'dd MMM', 'MMM', 'QQ FYY', 'FYY'],
            // day
            ['dd HH:mm:ss', 'dd HH:mm', 'dd HH:mm', 'dd', 'dd', '', '', ''],
            // hour
            ['HH:mm:ss', 'HH:mm', 'HH:mm', '', '', '', '', ''],
            // min
            ['mm:ss', 'mm', '', '', '', '', '', '']
        ];

        const longFormatTable = [
            // year
            ['dd MMMM yyyy HH:mm:ss', 'dd MMMM yyyy HH:mm', 'dd MMMM yyyy HH:mm', 'dd MMMM yyyy', 'dd MMMM yyyy', 'MMMM yyyy', 'QQQQ FYYYY', 'FYYYY'],
            // month
            ['dd MMMM HH:mm:ss', 'dd MMMM HH:mm', 'dd MMMM HH:mm', 'dd MMMM', 'dd MMMM', 'MMMM', 'QQQQ FYYYY', 'FYYYY'],
            // week
            ['dd HH:mm:ss', 'dd HH:mm', 'dd HH:mm', 'dd', 'dd MMMM', 'MMMM', 'QQQQ FYYYY', 'FYYYY'],
            // day
            ['dd HH:mm:ss', 'dd HH:mm', 'dd HH:mm', 'dd', 'dd', '', '', ''],
            // hour
            ['HH:mm:ss', 'HH:mm', 'HH:mm', '', '', '', '', ''],
            // min
            ['mm:ss', 'mm', '', '', '', '', '', '']
        ];

        const format = isShortFormat ? shortFormatTable[intervalIndex][frequencyIndex] : longFormatTable[intervalIndex][frequencyIndex];

        if (format) {
            return format;
        }

        return isShortFormat ? shortFormatTable[0][0] : longFormatTable[0][0];
    }

    export function formatPercentValue(value: any): string {
        return formatHelper.formatNumberByFormatName(value, constants.percentageNumberFormatName, constants.defaultPercentageFormat);
    }

    export function formatNumberByFormatName(value: any, formatName: string, defaultFormat?: string): string {
        const format = formatHelper.getFormat(formatName, defaultFormat);
        return formatHelper.formatNumberByFormat(value, format);
    }

    export function formatNumberByFormat(value: any, format: IFormat): string {
        if (_.isString(format)) {
            return formatHelper.formatNumber(value, format);
        }

        const locale = localizationHelper.userCultureInfo.locale;
        const localeFormat = format.localeFormats && format.localeFormats[locale] ? format.localeFormats[locale] : format.stringFormat;
        return format.intervalFormat ? formatHelper.formatSeconds(value, localeFormat) : formatHelper.formatNumber(value, localeFormat);
    }

    export function formatNumber(value: any, format: string): string {
        const formatter = new FormatDotNetStyle(localizationHelper.getTwoLettersLanguageName());

        if (_.isNil(format)) {
            return value;
        }

        if (isNaN(parseFloat(value)) || !isFinite(value)) {
            return _.toString(value);
        }

        return formatter.formatNumber(format, value);
    }

    export function formatDateTimeInterval(timeInterval: DateTimeInterval, formatName: string = null, withTime = false, asIs = false): string {
        const formatter = _.isNil(formatName)
            ? (date: Date, endInterval: boolean) => formatValueByDefault(date)
            : (date: Date, endInterval: boolean) => formatDateTime(date, formatName, endInterval, withTime, asIs);

        const fromText = formatter(timeInterval.from, false);
        const toText = formatter(timeInterval.to, true);
        return `${fromText} – ${toText}`;
    }

    export function getDateTimeFormat(formatName: string, withTime = false): DateTimeNamedFormat {
        const namedFormats = getMeta()
            .read(meta => meta.dateTimeFormat)
            .read(formats => formats.namedFormats)
            .valueOrDefault([]);

        const formatByName = _.find(namedFormats, f => f.name === formatName);
        if (formatByName) {
            return formatByName;
        }

        const defaultFormat = DateTimeNamedFormat.create();
        _.assign(defaultFormat, {
            format: withTime ? constants.defaultDateTimeFormat : constants.defaultDateFormat,
            withTime: withTime
        });
        return defaultFormat;
    }

    export function formatDateTime(dateTime, formatName: string, isEndOfInterval = false, withTime = false, asIs = false): string {
        const format = getDateTimeFormat(formatName, withTime);
        dateTime = new Date(dateTime);
        if (
            format &&
            !asIs &&
            isEndOfInterval &&
            !format.withTime &&
            timeHelper.nearestStartTime(dateTime, TimeInterval.day1).valueOf() === dateTime.valueOf()
        ) {
            dateTime.setDate(dateTime.getDate() - 1);
        }

        return formatDate(dateTime, format.format);
    }

    export function formatDate(date: Date, format: string, formatLocale: string = null): string {
        if (!_.isDate(date)) {
            return '';
        }

        const locale = formatLocale ? formatLocale : localizationHelper.getTwoLettersLanguageName();

        const formatter = new FormatDotNetStyle(locale);

        if (format.indexOf('FYY') !== -1) {
            format = format.replaceAll('FYYYY', 'yyyy');
            format = format.replaceAll('FYY', 'yy').trim();
        }

        format = formatter.formatDateTime(format, date);
        format = formatter.formatQuarter(format, getQuarter(date, 0));

        return format;
    }

    export function formatValueByInvariantCulture(value: any): string {
        if (_.isNil(value)) {
            return '';
        }
        if (_.isDate(value)) {
            return formatDate(value, constants.invariantCultureDateTimeFormat);
        } else {
            return value.toString();
        }
    }

    export function formatValueByDefault(value: any): string {
        if (_.isNil(value)) {
            return '';
        }
        if (_.isString(value)) {
            return value;
        } else if (_.isDate(value)) {
            if (altosoft.model && altosoft.model.insightMeta && altosoft.model.insightMeta.dateTimeFormat) {
                return formatDateTime(value, constants.defaultNamedFormatName);
            } else {
                return formatDate(value, localizationHelper.getInputDateTimeFormat(true));
            }
        } else {
            return formatNumber(value, constants.defaultNumberFormat);
        }
    }

    export function getQuarter(date: Date, firstMonth: number): number {
        const month = (date.getMonth() - firstMonth + 12) % 12;

        if (month <= 2) {
            return 1;
        } else if (month <= 5) {
            return 2;
        } else if (month <= 8) {
            return 3;
        } else if (month <= 11) {
            return 4;
        }

        return 1;
    }

    export function getTooltipFormat(componentType: ComponentType, formatName: string, isLabelFormat = false): string {
        const tooltipFormats = getMeta()
            .read(m => m.tooltipFormats)
            .valueOrDefault([]);
        const tooltipFormat = _.find(tooltipFormats, f => f.isLabelFormat === isLabelFormat && f.name === formatName);

        const format = isLabelFormat ? getDefaultLabelFormat(componentType) : getDefaultTooltipFormat(componentType);

        const defaultFormat = format || (isLabelFormat ? constants.defaultLabelFormat : constants.defaultTooltipFormat);

        if (!tooltipFormat) {
            return defaultFormat;
        }

        return (
            _(tooltipFormat.tooltipFormats)
                .filter(tf => tf.id === componentType)
                .map(tf => tf.value)
                .first() || defaultFormat
        );
    }

    export function hasLabel(componentType) {
        switch (componentType) {
            case ComponentType.area:
            case ComponentType.areaRange:
            case ComponentType.bar:
            case ComponentType.rangeBar:
            case ComponentType.pareto:
            case ComponentType.groupBar:
            case ComponentType.line:
            case ComponentType.spline:
            case ComponentType.splineArea:
            case ComponentType.stackedArea:
            case ComponentType.stackedArea100:
            case ComponentType.stackedBar:
            case ComponentType.stackedBar100:
            case ComponentType.stackedLine:
            case ComponentType.stackedSpline:
            case ComponentType.stackedSplineArea:
            case ComponentType.stackedSplineArea100:
            case ComponentType.splineRange:
            case ComponentType.candleStick:
            case ComponentType.stick:
            case ComponentType.horizontalBar:
            case ComponentType.horizontalRangeBar:
            case ComponentType.horizontalGroupBar:
            case ComponentType.horizontalStackedBar:
            case ComponentType.horizontalStackedBar100:
            case ComponentType.pie:
            case ComponentType.doughnut:
            case ComponentType.bubble:
            case ComponentType.dialGauge:
            case ComponentType.linearGauge:
            case ComponentType.trafficLight:
            case ComponentType.treeMap:
            case ComponentType.polarChartArea:
            case ComponentType.polarChartColumn:
            case ComponentType.polarChartLine:
            case ComponentType.polarChartScatter:
            case ComponentType.trellisChart:
            case ComponentType.pyramid:
            case ComponentType.funnel:
            case ComponentType.boxPlot:
            case ComponentType.pieSemiCircle:
            case ComponentType.waterfall:
                return true;
        }
        return false;
    }

    export function hasTooltip(componentType) {
        switch (componentType) {
            case ComponentType.area:
            case ComponentType.areaRange:
            case ComponentType.bar:
            case ComponentType.rangeBar:
            case ComponentType.pareto:
            case ComponentType.groupBar:
            case ComponentType.line:
            case ComponentType.spline:
            case ComponentType.splineArea:
            case ComponentType.stackedArea:
            case ComponentType.stackedArea100:
            case ComponentType.stackedBar:
            case ComponentType.stackedBar100:
            case ComponentType.stackedLine:
            case ComponentType.stackedSpline:
            case ComponentType.stackedSplineArea:
            case ComponentType.stackedSplineArea100:
            case ComponentType.splineRange:
            case ComponentType.candleStick:
            case ComponentType.stick:
            case ComponentType.horizontalBar:
            case ComponentType.horizontalRangeBar:
            case ComponentType.horizontalGroupBar:
            case ComponentType.horizontalStackedBar:
            case ComponentType.horizontalStackedBar100:
            case ComponentType.pie:
            case ComponentType.doughnut:
            case ComponentType.bubble:
            case ComponentType.scatter:
            case ComponentType.dialGauge:
            case ComponentType.linearGauge:
            case ComponentType.mapPin:
            case ComponentType.map:
            case ComponentType.trafficLight:
            case ComponentType.chartWithRange:
            case ComponentType.treeMap:
            case ComponentType.processControl:
            case ComponentType.polarChartArea:
            case ComponentType.polarChartColumn:
            case ComponentType.polarChartLine:
            case ComponentType.polarChartScatter:
            case ComponentType.trellisChart:
            case ComponentType.pyramid:
            case ComponentType.funnel:
            case ComponentType.boxPlot:
            case ComponentType.pieSemiCircle:
            case ComponentType.waterfall:
                return true;
        }
        return false;
    }

    export function getDefaultTooltipFormat(componentType) {
        switch (componentType) {
            case ComponentType.stackedArea:
            case ComponentType.stackedBar:
            case ComponentType.stackedLine:
            case ComponentType.stackedSplineArea:
            case ComponentType.horizontalStackedBar:
            case ComponentType.horizontalStackedBar100:
            case ComponentType.stackedSplineArea100:
            case ComponentType.stackedArea100:
            case ComponentType.stackedBar100:
                return '<Name>\n<Result>\n<PercentResult>\n<From> - <To>';

            case ComponentType.line:
            case ComponentType.bar:
            case ComponentType.rangeBar:
            case ComponentType.groupBar:
            case ComponentType.pareto:
            case ComponentType.area:
            case ComponentType.spline:
            case ComponentType.splineArea:
            case ComponentType.stackedSpline:
            case ComponentType.horizontalBar:
            case ComponentType.horizontalRangeBar:
            case ComponentType.horizontalGroupBar:
            case ComponentType.processControl:
            case ComponentType.polarChartArea:
            case ComponentType.polarChartColumn:
            case ComponentType.polarChartLine:
            case ComponentType.polarChartScatter:
            case ComponentType.trellisChart:
            case ComponentType.processTrellisChart:
            case ComponentType.pyramid:
            case ComponentType.funnel:
                return '<Name>\n<Result>\n<From> - <To>';

            case ComponentType.range:
            case ComponentType.splineRange:
            case ComponentType.areaRange:
                return '<Name>\nTop: <Result:0>\nBottom: <Result:1>\n<From> - <To>';

            case ComponentType.pie:
            case ComponentType.doughnut:
            case ComponentType.pieSemiCircle:
            case ComponentType.processAgingChart:
                return '<Name>\n<Result>\n<PercentResult>\n<From> - <To>';

            case ComponentType.trafficLight:
                return '<Name>\n<Result>\n<From> - <To>';

            case ComponentType.metricGrid:
            case ComponentType.recordGrid:
                return '<From> - <To>';

            case ComponentType.bubble:
                return '<Name>\nX: <Result:0>\nY: <Result:1>\nSize: <Result:2>\n<From> - <To>';

            case ComponentType.scatter:
                return '<Name:0>: <Result:0>\n<Name:1>: <Result:1>\n<ScatterBreakdown>\n<DisplayedFields>';

            case ComponentType.candleStick:
            case ComponentType.stick:
                return '<Name>\nTop range: <Result:0>\nTop bar: <Result:1>\nBottom bar: <Result:2>\nBottom range: <Result:3>\n<From> - <To>';

            case ComponentType.boxPlot:
                return '<Name>\nLargest observation: <Result:0>\nUpper quartile: <Result:1>\nMedian: <Result:2>\nLower quartile: <Result:3>\nSmallest observation: <Result:4>\n<From> - <To>';

            case ComponentType.mapPin:
                return '<Name>\n<Dimension>: <Value>\n<DisplayedFields>';
            case ComponentType.map:
                return '<Name>\n<Value>\n<Result>\n<From> - <To>';

            case ComponentType.recordTime:
                return '<Name>\n<From> - <To>';

            case ComponentType.chartWithRange:
                return '<Name>\n<Result:1>\nRange: <Result:0>\n<From> - <To>';

            case ComponentType.treeMap:
                return '<Dimension:1>: <Value:1>\n<Dimension:0>: <Value:0>\n<Result>\n<From> - <To>';

            case ComponentType.dialGauge:
            case ComponentType.linearGauge:
                return '<Name>\n<Result>\n<From> - <To>';
            case ComponentType.waterfall:
                return '<Dimension>: <Value>\n<Result:0>\n<Result:1>\n<From> - <To>';
        }

        return constants.defaultTooltipFormat;
    }

    export function getDefaultLabelFormat(componentType) {
        switch (componentType) {
            case ComponentType.range:
            case ComponentType.candleStick:
            case ComponentType.stick:
                return '';
            case ComponentType.bubble:
                return '<Name>';
            case ComponentType.treeMap:
                return '<Value:0>';
            default:
                return '<Result>';
        }
    }

    export function formatTooltip(format: string, point, percentResult: any = null): string {
        if (_.isNil(point)) {
            return null;
        }

        const name = point.name;
        const from = point.from;
        const to = point.to;
        const result = point.result;
        const value = point.dimensionValue;
        const dimension = point.dimensionName;

        const results = point.results || [];
        const formats = point.formats || [];
        const displayFields = point.displayFields || [];
        const names = point.names || [];
        const dimensions = point.dimensions || [];

        const safeValue = v => (_.isNil(v) ? '' : v);

        const replacer = (res: string, temp: string, v) => res.replaceAll(temp, safeValue(v));

        let tooltip = translate(format);
        tooltip = replacer(tooltip, '<Name>', name);
        tooltip = replacer(tooltip, '<Result>', result);

        results.forEach((item, index) => {
            tooltip = tooltip.replaceAll(`<Result:${index}>`, formats[index] ? formatNumberByFormat(item, formats[index]) : item);
        });

        names.forEach((item, index) => {
            tooltip = replacer(tooltip, `<Name:${index}>`, item);
        });

        dimensions.forEach((item, index) => {
            tooltip = replacer(tooltip, `<Dimension:${index}>`, item.title);
            tooltip = replacer(tooltip, `<Value:${index}>`, item.value);
        });

        const displayFieldsString = displayFields
            .map(item => {
                return item.name + ': ' + point.row.data[item.index].value;
            })
            .join('\n');

        tooltip = replacer(tooltip, '<DisplayedFields>', displayFieldsString);
        tooltip = tooltip.replaceAll('<ScatterBreakdown>', _.isEmpty(names[2]) ? '' : `${names[2]}: ${results[2]}`);
        tooltip = replacer(tooltip, '<PercentResult>', percentResult);
        if (point.metric && point.metric.description) {
            tooltip = replacer(tooltip, '<Description>', point.metric.description);
        }
        tooltip = replacer(tooltip, '<Description>', '');
        tooltip = replaceTooltipDate(tooltip, 'From', from);
        tooltip = replaceTooltipDate(tooltip, 'To', to, true);
        tooltip = replaceTooltipDate(tooltip, 'LastUpdatedTime', new Date());
        tooltip = replacer(tooltip, '<DataLoadTime>', '-');
        tooltip = replacer(tooltip, '<Dimension>', dimension);
        tooltip = replacer(tooltip, '<Value>', value);
        tooltip = tooltip.replaceAll('\n\n', '\n');
        tooltip = tooltip.replaceAll('\n', '<br>');
        tooltip = tooltip.replaceAll('<br><br>', '<br>');
        return tooltip;
    }

    function translate(format: string): string {
        const pairs = _(strings.formatStings as {})
            .map((value: string, key: string) => ({ key, value }))
            .orderBy(p => p.key.length, 'desc')
            .value();
        for (const p of pairs) {
            const replacer = new RegExp(`\\b(${p.key})\\b`, 'g');
            format = format.replace(replacer, p.value);
        }
        return format;
    }

    function replaceTooltipDate(tooltip, name, date, isEndOfInterval?) {
        const regExp = new RegExp(`<${name}:?(.*?)>`);

        return tooltip.replace(regExp, (match, group) =>
            _.isNil(date) ? '' : formatDateTime(date, group || constants.defaultNamedFormatName, isEndOfInterval)
        );
    }

    function getMeta(): IMaybe<InsightMetaModel> {
        return Maybe(altosoft.model).read<InsightMetaModel>(m => m.insightMeta);
    }
}
Date.prototype.format = function (format?: string) {
    //HACK:
    return _.isUndefined(format) ? this.toString() : formatHelper.formatDate(this, format);
};
Number.prototype.format = function (format: string) {
    // HACK:
    return formatHelper.formatNumber(this, format);
};

export default formatHelper;
